/**
 * THE DISPATCH BOARD — a manager's outbox, not a report.
 *
 * Brian: "I need to export the different stages of where we are and asking for
 * a review so that I can quickly shoot a message to each team member sorting by
 * representative and make sure they know these people need to be asked for a
 * review."
 *
 * So the screen is a stack of BLOCKS, one per rep, each with a Copy button that
 * puts a finished message on the clipboard. There is no sorting to do, no
 * filter to set and nothing to assemble afterwards — if there were, the export
 * would have failed at the only thing it is for.
 *
 * ⚠️ THIS READS FOUR FEEDS AND WRITES NOTHING. Every route it touches is an
 * existing GET already called elsewhere in this module, so there is no backend
 * change (hard rule #4) and no new door for check-doors.mjs to find. It is a
 * READ, gated on the module's own read action, and nothing here is audited as a
 * write because nothing here is one.
 *
 * ⚠️ WHY THE GATE IS `REVIEWS_VIEW` AND NOT SOMETHING STRICTER.
 * Every record on this screen is already reachable by the same person one tab
 * to the left: the Ask queue's "Everyone" scope shows the whole firm's book to
 * anyone who can open the module. This view re-ARRANGES that data; it does not
 * widen it. A stricter gate here would hide a rearrangement of something the
 * viewer can already read — navigation theatre, the exact thing PRESENTATIONS_
 * VIEW and AITOOLS_VIEW are documented as refusing to do. The manager-ness of
 * this screen is in its SHAPE, not in its access. Inventing a `reviews.dispatch`
 * action to make the gate look sharper would be inventing a permission, which
 * is worse.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api.js'
import { useAccess } from '../../lib/access.jsx'
import { ACTIONS } from '../../lib/rbac.js'
import { toCsv, downloadCsv } from '../../lib/csvExport.js'
import { coverageTally } from './reviewCoverage.js'
import {
  DISPATCH_STAGES, STAGE_TO_ASK, STAGE_FLAGGED,
  fromQueueRow, fromAskedRow, fromLandedRow, fromFlaggedRow,
  groupByRep, dispatchTally, repMessage, dispatchRows, dispatchFilename,
  NO_OWNER,
} from './dispatch.js'
import s from './reviews.module.css'

const n = (v) => (v || 0).toLocaleString()

/** Clipboard with a real fallback — a Copy button that silently does nothing is
 *  worse than no Copy button, because the manager pastes stale text. */
async function copyText(text) {
  try {
    if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true }
  } catch { /* fall through to the textarea path */ }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch { return false }
}

export default function DispatchBoard() {
  const access = useAccess()
  const can = access?.can || (() => true)
  const why = access?.why || (() => 'Your role does not include this.')

  const allowed = can(ACTIONS.REVIEWS_VIEW)

  const [state, setState] = useState('loading') // loading | ready | error
  const [err, setErr] = useState(null)
  const [feeds, setFeeds] = useState(null)
  const [open, setOpen] = useState({})
  const [copied, setCopied] = useState(null)

  const load = useCallback(async () => {
    setState('loading'); setErr(null)
    /* Each feed is settled INDEPENDENTLY. One failing route must cost its own
     * section and nothing else — a manager with three of four stages is still
     * able to send three messages, where a single rejected promise would hand
     * them an error page and no dispatch at all. */
    const [queue, asked, landed, flagged, links] = await Promise.allSettled([
      api.reviewQueue(),
      api.reviews({ filter: 'outstanding' }),
      api.reviews({ filter: 'landed' }),
      /* The suppressed ROWS. `?state=` is an existing view on an existing route
       * (CoveragePanel already uses `state=missed`), but `suppressed` is not a
       * state this repo can prove the backend serves — sr-portal-backend is a
       * separate repo. So it is ATTEMPTED, and its absence is reported as a
       * shortfall against the tally rather than drawn as "no flagged accounts",
       * which would be the dangerous reading. */
      api.reviewQueue({ state: 'suppressed' }),
      api.reviewLinks(),
    ])

    if (queue.status !== 'fulfilled') {
      setState('error')
      setErr(queue.reason?.message || 'Could not read the ask queue.')
      return
    }
    setFeeds({
      queue: queue.value,
      asked: asked.status === 'fulfilled' ? asked.value : null,
      askedErr: asked.status === 'rejected' ? (asked.reason?.message || 'unavailable') : null,
      landed: landed.status === 'fulfilled' ? landed.value : null,
      landedErr: landed.status === 'rejected' ? (landed.reason?.message || 'unavailable') : null,
      flagged: flagged.status === 'fulfilled' ? flagged.value : null,
      flaggedErr: flagged.status === 'rejected' ? (flagged.reason?.message || 'unavailable') : null,
      links: links.status === 'fulfilled' ? links.value : null,
    })
    setState('ready')
  }, [])

  useEffect(() => { if (allowed) load() }, [allowed, load])

  const model = useMemo(() => {
    if (!feeds) return null
    const queueRows = Array.isArray(feeds.queue?.queue) ? feeds.queue.queue : []
    const askedRows = Array.isArray(feeds.asked?.reviews) ? feeds.asked.reviews : []
    const landedRows = Array.isArray(feeds.landed?.reviews) ? feeds.landed.reviews : []
    const flaggedRows = Array.isArray(feeds.flagged?.queue) ? feeds.flagged.queue : []

    const records = [
      ...queueRows.map(fromQueueRow),
      ...askedRows.map(fromAskedRow),
      ...landedRows.map(fromLandedRow),
      ...flaggedRows.map(fromFlaggedRow),
    ]
    /* Dedupe by stage+id: a flagged order can legitimately arrive from BOTH the
     * queue (carrying a marker) and the explicit suppressed feed, and listing a
     * do-not-ask account twice in one message makes the section look padded. */
    const seen = new Set()
    const deduped = records.filter((r) => {
      const k = `${r.stage}:${r.id || r.account}`
      if (seen.has(k)) return false
      seen.add(k); return true
    })

    const blocks = groupByRep(deduped)
    const cov = coverageTally(feeds.queue?.tally)
    const flaggedCounted = cov.overlays.find((o) => o.key === 'suppressed')?.count ?? null
    return { records: deduped, blocks, tally: dispatchTally(blocks, deduped, flaggedCounted) }
  }, [feeds])

  const doCopy = async (block) => {
    const ok = await copyText(repMessage(block, { links: feeds?.links }))
    setCopied(ok ? block.key : `fail:${block.key}`)
    window.setTimeout(() => setCopied(null), 4000)
  }

  const doCsv = () => {
    if (!model) return
    downloadCsv(dispatchFilename(), toCsv(dispatchRows(model.blocks)))
  }

  if (!allowed) {
    return (
      <div className={s.wrap}>
        <div className={s.empty}>
          <strong>You cannot open the dispatch list.</strong>
          <br />
          {why(ACTIONS.REVIEWS_VIEW)}
        </div>
      </div>
    )
  }

  if (state === 'loading') return <div className={s.wrap}><div className={s.loadingLine}>Reading the four feeds…</div></div>

  if (state === 'error') {
    return (
      <div className={s.wrap}>
        <div className={s.warn}>
          <strong>The ask queue could not be read, so there is no dispatch list.</strong>{' '}
          {err}{' '}
          <button type="button" className={s.linkBtn} onClick={load}>Try again</button>
        </div>
      </div>
    )
  }

  const t = model.tally

  return (
    <div className={s.wrap}>

      <p className={s.rule}>
        One block per rep, ready to paste. Copy it and paste it wherever you message
        your team — there is nothing to sort, filter or assemble afterwards. The accounts somebody
        deliberately flagged <strong>do not ask</strong> are in their own section at the
        bottom of every block, with the reason, and never in the ask list.
      </p>

      <div className={s.dispatchBar}>
        <button type="button" className={s.btn} onClick={doCsv}>
          Download the CSV — {n(t.placements)} rows
        </button>
        <button type="button" className={`${s.btn} ${s.ghost}`} onClick={load}>Refresh</button>
      </div>

      {/* ── THE RECONCILIATION, STATED ON SCREEN ──────────────────────────
          A per-rep breakdown that silently drops a record is the failure mode
          here, and it is the same class as the summary bar that under-reported
          by exactly its non-primary buckets and taught an auditor that 203 rows
          were missing. So this states the property that was CHECKED. */}
      {t.reconciled ? (
        <p className={s.covFooter}>
          Reconciled — all <b>{n(t.records)}</b> records reached a block,
          across <b>{n(t.reps)}</b> rep{t.reps === 1 ? '' : 's'}
          {t.unassignedBlock ? ` plus ${n(t.unassignedBlock.total)} with nobody assigned` : ''}.
          {t.shared > 0 && (
            <> {n(t.placements)} lines are printed rather than {n(t.records)} because{' '}
              <b>{n(t.shared)}</b> order{t.shared === 1 ? ' names' : 's name'} more than one rep
              and both are told. That is why the per-rep counts sum high — it is not double
              counting.</>
          )}
          {' '}No account is in both an ask list and the do-not-ask list.
        </p>
      ) : (
        <p className={`${s.covFooter} ${s.covFooterBad}`} role="alert">
          <strong>RECONCILIATION FAILED — do not send these blocks.</strong>{' '}
          {t.dropped > 0 && <>{n(t.dropped)} record{t.dropped === 1 ? '' : 's'} reached no block at all. </>}
          {t.stray.length > 0 && <>Unknown stage{t.stray.length === 1 ? '' : 's'}: {[...new Set(t.stray)].join(', ')}. </>}
          {t.collisions.length > 0 && (
            <><b>{n(t.collisions.length)} account{t.collisions.length === 1 ? ' is' : 's are'} in
            BOTH an ask list and the do-not-ask list.</b> That is the one thing this export
            exists to make impossible. </>
          )}
        </p>
      )}

      {/* THE FLAGGED SHORTFALL. Reported loudly, because a do-not-ask account
          this export could not list is an account a rep might still be handed. */}
      {t.flaggedShort > 0 && (
        <div className={s.warn} role="alert">
          <strong>The do-not-ask list may be incomplete.</strong> Coverage counts{' '}
          <b>{n(t.flaggedCounted)}</b> flagged account{t.flaggedCounted === 1 ? '' : 's'} and this
          export could list only <b>{n(t.flaggedListed)}</b>. The queue route does not return
          suppressed rows, so <b>{n(t.flaggedShort)}</b> flagged account
          {t.flaggedShort === 1 ? '' : 's'} cannot be named here. Check the Ask queue before
          sending if an account looks wrong.
          {feeds.flaggedErr && <> (The suppressed-row read answered: {feeds.flaggedErr}.)</>}
        </div>
      )}

      {(feeds.askedErr || feeds.landedErr) && (
        <div className={s.warn}>
          A feed is missing, so those stages are absent rather than empty —
          {feeds.askedErr && ` "already asked" (${feeds.askedErr})`}
          {feeds.askedErr && feeds.landedErr && ' and'}
          {feeds.landedErr && ` "landed" (${feeds.landedErr})`}.
          The ask list itself is unaffected.
        </div>
      )}

      {/* ── THE BLOCKS ─────────────────────────────────────────────────── */}
      {!model.blocks.length ? (
        <div className={s.empty}>
          <strong>Nothing to dispatch.</strong>
          <br />
          No order is in any review stage today. That is a real clear board, not a failed
          read — the feeds answered.
        </div>
      ) : model.blocks.map((b) => {
        const isOpen = !!open[b.key]
        return (
          <section
            key={b.key}
            className={`${s.dispatchBlock} ${b.unassigned ? s.dispatchUnassigned : ''}`}
            aria-label={`Dispatch for ${b.unassigned ? NO_OWNER : b.rep}`}
          >
            <div className={s.dispatchHead}>
              <div className={s.dispatchWho}>
                <div className={s.dispatchRep}>{b.unassigned ? NO_OWNER : b.rep}</div>
                <div className={s.dispatchCounts}>
                  {DISPATCH_STAGES.filter((st) => b.counts[st.key] > 0).map((st) => (
                    <span
                      key={st.key}
                      className={st.key === STAGE_FLAGGED ? s.dispatchFlagCount : ''}
                    >
                      {n(b.counts[st.key])} {st.label.toLowerCase()}
                    </span>
                  ))}
                </div>
              </div>
              <button type="button" className={s.btn} onClick={() => doCopy(b)}>
                {copied === b.key ? 'Copied' : copied === `fail:${b.key}` ? 'Copy failed' : 'Copy message'}
              </button>
            </div>

            {b.unassigned && (
              <p className={s.dispatchNote}>
                These orders name no rep, so nobody picks them up on their own. This block is
                addressed to you.
              </p>
            )}

            {/* The flagged section is ALWAYS drawn expanded, above the fold of
                the disclosure, never inside it. A do-not-ask account behind a
                "show more" is a do-not-ask account nobody reads. */}
            {b.counts[STAGE_FLAGGED] > 0 && (
              <div className={s.dispatchFlagged}>
                <div className={s.dispatchFlagHead}>
                  DO NOT ASK ({n(b.counts[STAGE_FLAGGED])}) — flagged by a person
                </div>
                <ul className={s.dispatchList}>
                  {b.stages[STAGE_FLAGGED].map((r) => (
                    <li key={r.id || r.account} className={s.dispatchLine}>
                      <span className={s.dispatchAccount}>
                        {r.url
                          ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.account}</a>
                          : r.account}
                      </span>
                      <span className={s.dispatchReason}>{r.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="button"
              className={s.notesToggle}
              aria-expanded={isOpen}
              onClick={() => setOpen((m) => ({ ...m, [b.key]: !isOpen }))}
            >
              {isOpen ? 'Hide' : 'Show'} the {n(b.counts[STAGE_TO_ASK])} to ask
              {b.total - b.counts[STAGE_TO_ASK] - b.counts[STAGE_FLAGGED] > 0
                ? ` and ${n(b.total - b.counts[STAGE_TO_ASK] - b.counts[STAGE_FLAGGED])} already under way`
                : ''}
            </button>

            {isOpen && DISPATCH_STAGES.filter((st) => st.key !== STAGE_FLAGGED).map((st) => (
              b.counts[st.key] === 0 ? null : (
                <div key={st.key} className={s.dispatchStage}>
                  <div className={s.dispatchStageHead}>{st.heading} ({n(b.counts[st.key])})</div>
                  <ul className={s.dispatchList}>
                    {b.stages[st.key].map((r) => (
                      <li key={r.id || r.account} className={s.dispatchLine}>
                        <span className={s.dispatchAccount}>
                          {r.url
                            ? <a href={r.url} target="_blank" rel="noopener noreferrer">{r.account}</a>
                            : r.account}
                        </span>
                        <span className={s.dispatchDetail}>
                          {r.contact
                            ? `${r.contact}${r.contactEmail ? ` · ${r.contactEmail}` : ' · no email on the order'}`
                            : st.act ? 'no named contact on the order' : ''}
                        </span>
                        <span className={s.dispatchDetail}>
                          {[
                            r.ordered,
                            r.deliveredDay && `delivered ${r.deliveredDay}${r.daysSince != null ? ` · ${r.daysSince}d ago` : ''}`,
                            r.stateWord,
                            r.ask.length && `ask for ${r.ask.join(', ')}`,
                            r.note,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            ))}
          </section>
        )
      })}

      <p className={s.note}>
        A rep name comes from the order where the order names one, and otherwise from whoever
        made the ask — the CSV says which, per row, so nobody has to guess. Orders naming
        nobody are gathered into their own block rather than dropped, because those are the
        ones that stay unactioned.
      </p>
    </div>
  )
}
